import React, { useEffect, useRef } from "react";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { Box, MenuItem, Select } from "@mui/material";
import { STATUS_VARIABLE } from "../../../utils/constant";


import {
  useAccessoryCategoryProvider,
  useAccessoryUnitProvider,
  useClientProvider,
  useColorsProvider,
  useCompaniesProvider,
  useFactoriesProvider,
  useMachinesProvider,
  useMerchandiserProvider,
  useSalesProvider,
  useShipmentTermProvider,
} from "../../../hooks/useApiProviders";
import { CustomAutocompleteAddOption } from "../CustomAutoCompleteAddOption";
import { useIncoterms, usePaymentTerm, useShippingMode, useSuppliers } from "../../../modules/orders/hooks/useProvider";

interface Props {
  name: string;
  disabled: boolean;
  location: string[] | undefined;
  params: GridRenderEditCellParams;
  onChange: (name: string, value: string) => void;
  defaultValue?: string;
  onSearchChange?: (value: string) => void; 
}

const props = {
  autoFocus: true,
  displayEmpty: true,
  sx: {
    border: "none",
    outline: "none",
    ".MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  },
};

export const RenderLocationCell: React.FC<Props> = ({
  name,
  params,
  disabled,
  location,
  defaultValue,
  onChange,
}) => {
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const rowId = params.id;

  const focusedCell = apiRef.current.state.focus?.cell;
  const isFocused =
    focusedCell?.id === rowId && focusedCell?.field === "location";

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isFocused]);
  return (
    <Box mt={1} width={"100%"}>
      <CustomAutocompleteAddOption
        onChangeValue={onChange}
        disabled={disabled}
        label="location"
        data={[defaultValue ?? "", ...[...(location ?? [])]]}
        value={params.value}
        name={name}
        inputRef={inputRef}
      />
    </Box>
  );
};

export const RenderDestinationCell: React.FC<Props> = ({
  name,
  params,
  disabled,
  location,
  defaultValue,
  onChange,
}) => {
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const rowId = params.id;

  const focusedCell = apiRef.current.state.focus?.cell;
  const isFocused =
    focusedCell?.id === rowId && focusedCell?.field === "destination_location";

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isFocused]);
  return (
    <Box mt={1} width={"100%"}>
      <CustomAutocompleteAddOption
        onChangeValue={onChange}
        disabled={disabled}
        // label="location"
        data={[defaultValue ?? "", ...[...(location ?? [])]]}
        value={params.value}
        name={name}
        inputRef={inputRef}
      />
    </Box>
  );
};

export const RenderReferenceCell: React.FC<Props> = ({
  name,
  params,
  disabled,
  location,
  onChange,
  onSearchChange,
}) => {
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const rowId = params.id;

  const focusedCell = apiRef.current.state.focus?.cell;
  const isFocused =
    focusedCell?.id === rowId && focusedCell?.field === "reference";

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isFocused]);

  return (
    <Box mt={1} width="100%">
      <CustomAutocompleteAddOption
        onChangeValue={onChange}
        onSearchChange={onSearchChange} 
        disabled={disabled}
        data={location ?? []}
        value={params.row.reference ?? ""}
        name={name}
        inputRef={inputRef}
      />
    </Box>
  );
};

export const RenderCustomer = (params: GridRenderEditCellParams) => {
  const { data: clientData } = useClientProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {clientData &&
        clientData.map((item, index) => (
          <MenuItem key={index} value={item.customer_name}>
            {item.customer_name}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderMerchandiser = (params: GridRenderEditCellParams) => {
  const { data: merchandiserData } = useMerchandiserProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {merchandiserData &&
        merchandiserData.map((item, index) => (
          <MenuItem key={index} value={item.description}>
            {item.description}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderSalesPeople = (params: GridRenderEditCellParams) => {
  const { data: salesData } = useSalesProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {salesData &&
        salesData.map((item, index) => (
          <MenuItem key={index} value={item.name}>
            {item.name}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderStatus = (params: GridRenderEditCellParams) => {
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {STATUS_VARIABLE.map((item, index) => (
        <MenuItem key={index} value={item}>
          {item}
        </MenuItem>
      ))}
    </Select>
  );
};

export const RenderShipmentMode = (params: GridRenderEditCellParams) => {
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      <MenuItem value={"AIR"}>AIR</MenuItem>
      <MenuItem value={"SEA"}>SEA</MenuItem>
      <MenuItem value={"LOCAL"}>LOCAL</MenuItem>
    </Select>
  );
};

export const RenderShipmentTerm = (params: GridRenderEditCellParams) => {
  const { data: shipmentTermData } = useShipmentTermProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {shipmentTermData &&
        shipmentTermData.map((item, index) => (
          <MenuItem key={index} value={item.code}>
            {item.code}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderFactory = (params: GridRenderEditCellParams) => {
  const { data: factoriesData } = useFactoriesProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {factoriesData &&
        factoriesData.map((item, index) => (
          <MenuItem key={index} value={item.code}>
            {item.code}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderInvoice = (params: GridRenderEditCellParams) => {
  const { data: companiesData } = useCompaniesProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {companiesData &&
        companiesData.map((item, index) => (
          <MenuItem key={index} value={item.code}>
            {item.code}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderMachine = (params: GridRenderEditCellParams) => {
  const { data: machinesData } = useMachinesProvider();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {machinesData &&
        machinesData.map((item, index) => (
          <MenuItem key={index} value={item.code}>
            {item.code}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderStyleCategory = (params: GridRenderEditCellParams) => {
  const { data: styleCategoryData } = useAccessoryCategoryProvider();
  const [localValue, setLocalValue] = React.useState(
    params.row?.categorie?.id ?? ""
  );

  return (
    <Select
      value={localValue}
      onChange={(event) => {
        const newCategoryId = event.target.value as number;
        setLocalValue(newCategoryId);

        const newCategory =
          styleCategoryData &&
          styleCategoryData.find((c) => c.id === newCategoryId);

        if (newCategory) {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newCategory.id,
          });
        }
      }}
      {...props}
    >
      {styleCategoryData &&
        styleCategoryData.map((item, index) => (
          <MenuItem key={index} value={item.id}>
            {item.description}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderStyleUnit = (params: GridRenderEditCellParams) => {
  const { data: styleUnitData } = useAccessoryUnitProvider();
  const [localValue, setLocalValue] = React.useState(
    params.row?.unit?.id ?? ""
  );
  return (
    <Select
      value={localValue}
      onChange={(event) => {
        setLocalValue(event.target.value as number);
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        });
      }}
      {...props}
    >
      {styleUnitData &&
        styleUnitData.map((item, index) => (
          <MenuItem key={index} value={item.id}>
            {item.description}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderColor = (params: GridRenderEditCellParams) => {
  const { data: colorsData } = useColorsProvider();
  const [localValue, setLocalValue] = React.useState(
    params.row?.unit?.id ?? ""
  );
  return (
    <Select
      value={localValue}
      onChange={(event) => {
        setLocalValue(event.target.value as number);
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        });
      }}
      {...props}
    >
      {colorsData &&
        colorsData.map((item, index) => (
          <MenuItem key={index} value={item.id}>
            {item.color_name}
          </MenuItem>
        ))}
    </Select>
  );
};

export const RenderSupplier = (params: GridRenderEditCellParams) => {
  const { data: suppliers } = useSuppliers();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {suppliers?.map((item, index) => (
        <MenuItem key={index} value={item.fullname}>
          {item.name}
        </MenuItem>
      ))}
    </Select>
  );
};

export const RenderIncoterm = (params: GridRenderEditCellParams) => {
  const { data: incoterm } = useIncoterms();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {incoterm?.map((item, index) => (
        <MenuItem key={index} value={item.label}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export const RenderPaymentTerm = (params: GridRenderEditCellParams) => {
  const { data: paymentTerm } = usePaymentTerm();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {paymentTerm?.map((item, index) => (
        <MenuItem key={index} value={item.description}>
          {item.description}
        </MenuItem>
      ))}
    </Select>
  );
};

export const RenderShippingMode = (params: GridRenderEditCellParams) => {
  const { data: shippingMode } = useShippingMode();
  return (
    <Select
      value={params.value}
      onChange={(event) =>
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        })
      }
      {...props}
    >
      {shippingMode?.map((item, index) => (
        <MenuItem key={index} value={item.label}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  );
};
